import { useCallback, useRef } from 'react'
import type { POICandidate, Position } from '../types'
import { useAppStore } from '../store/appStore'
import L from 'leaflet'

export interface Route {
  start: Position
  end: { lat: number; lon: number }
  poi: POICandidate
  polyline: L.Polyline | null
}

export function useRoute() {
  const store = useAppStore()
  const routeRef = useRef<Route | null>(null)
  const mapRef = useRef<L.Map | null>(null)

  const setMapRef = useCallback((map: L.Map | null) => {
    mapRef.current = map
  }, [])

  const planRoute = useCallback(
    (poi: POICandidate) => {
      const position = store.getPosition()
      if (!position || !mapRef.current) return

      // Ensure map is fully initialized and ready
      if (!mapRef.current.getContainer() || !mapRef.current.getContainer().offsetParent) {
        // Map not ready yet, wait a bit
        setTimeout(() => planRoute(poi), 100)
        return
      }

      // Clear existing route
      if (routeRef.current?.polyline) {
        try {
          mapRef.current.removeLayer(routeRef.current.polyline)
        } catch (e) {
          // Layer might already be removed
        }
      }

      // Create route line - use whenReady to ensure map is fully loaded
      const start: [number, number] = [position.lat, position.lon]
      const end: [number, number] = [poi.lat, poi.lon]

      mapRef.current.whenReady(() => {
        if (!mapRef.current) return

        try {
          const polyline = L.polyline([start, end], {
            color: '#5aa7ff',
            weight: 4,
            opacity: 0.7,
            dashArray: '10, 10',
          }).addTo(mapRef.current)

          // Add markers with better styling
          const startMarker = L.marker(start, {
            icon: L.divIcon({
              className: 'route-start-marker',
              html: '<div style="width: 20px; height: 20px; background: #5aa7ff; border-radius: 50%; border: 3px solid #0b0d10; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
              iconSize: [20, 20],
              iconAnchor: [10, 10],
            }),
          }).addTo(mapRef.current)
          startMarker.bindTooltip('Your location', { permanent: false, direction: 'top' })

          const endMarker = L.marker(end, {
            icon: L.divIcon({
              className: 'route-end-marker',
              html: '<div style="width: 24px; height: 24px; background: #ff5a5a; border-radius: 50%; border: 3px solid #0b0d10; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
              iconSize: [24, 24],
              iconAnchor: [12, 12],
            }),
          }).addTo(mapRef.current)
          endMarker.bindTooltip(poi.title, { permanent: false, direction: 'top' })

          // Fit bounds to show entire route
          mapRef.current.fitBounds([start, end], {
            padding: [50, 50],
          })

          routeRef.current = {
            start: position,
            end: { lat: poi.lat, lon: poi.lon },
            poi,
            polyline,
          }

          // Store markers for cleanup
          ;(routeRef.current as any).startMarker = startMarker
          ;(routeRef.current as any).endMarker = endMarker
        } catch (error) {
          console.error('Error adding route to map:', error)
        }
      })
    },
    [store]
  )

  const clearRoute = useCallback(() => {
    if (!mapRef.current || !routeRef.current) return

    if (routeRef.current.polyline) {
      mapRef.current.removeLayer(routeRef.current.polyline)
    }
    if ((routeRef.current as any).startMarker) {
      mapRef.current.removeLayer((routeRef.current as any).startMarker)
    }
    if ((routeRef.current as any).endMarker) {
      mapRef.current.removeLayer((routeRef.current as any).endMarker)
    }

    routeRef.current = null
  }, [])

  return {
    planRoute,
    clearRoute,
    setMapRef,
    currentRoute: routeRef.current,
  }
}
